import { IDevice, IMLPredictDevice } from "@multiconsult-group/multiplacer-core";
import { openapi, type PostMultiplacerDevicesApiArg } from "../../../openapi";
import { IRootState } from "../../app/store";
import {
  addNonPublischedDevicesFromAlgosInRedux,
  ISpaceValidationRun,
  thunkRunSpaceValidations,
  thunkToast,
} from "../designSlice";
import { convertMlBatchToDevices, IDeviceContext } from "../designThunk";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";

const getDevicesCacheArgForDevice = (
  device: Pick<IDevice, "floor" | "projectNo">
): PostMultiplacerDevicesApiArg => ({
  body: {
    projectNo: Number(device.projectNo),
    floor: device.floor,
  },
});

const getDeviceListCacheArgsContainingDevice = (
  state: IRootState,
  deviceId: string | undefined
): PostMultiplacerDevicesApiArg[] => {
  if (!deviceId) return [];

  return Object.values(state.api.queries)
    .filter(
      (query: any) =>
        query?.endpointName === "postMultiplacerDevices" && query?.status === "fulfilled"
    )
    .map((query: any) => query.originalArgs as PostMultiplacerDevicesApiArg | undefined)
    .filter((args): args is PostMultiplacerDevicesApiArg => args != null)
    .filter((args) => {
      const devices = openapi.endpoints.postMultiplacerDevices.select(args)(state).data;
      return devices?.some((device) => device._id === deviceId) ?? false;
    });
};

export const deviceApiEnhancer = openapi.enhanceEndpoints({
  endpoints: {
    postMultiplacerDevices: {
      providesTags: ["devices"],
    },

    postMultiplacerDevicesSimilarRoomsWithDevices: {
      providesTags: ["similarRoomsWithDesign"],
    },

    postMultiplacerDevicesSimilarRoomsWithIfcDesign: {
      providesTags: ["similarRoomsWithDesign"],
    },

    postMultiplacerDeviceDeleteInRooms: {
      invalidatesTags: ["devices"],

      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          dispatch(
            thunkToast({
              text: "Komponenter ble slettet",
              header: "Suksess",
              isSuccess: true,
            })
          );
        } catch (e) {
          dispatch(
            thunkToast({
              text: "Kunne ikke slette komponenter",
              header: "Feil",
              isSuccess: false,
            })
          );
        }
      },
    },

    postMultiplacerDeviceAddDevices: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          const addedDevices = (await queryFulfilled).data;
          const state = getState() as IRootState;
          const spaceValidationRun = state.design.spaceValidationRun as ISpaceValidationRun;
          const devicesByCacheArg = new Map<
            string,
            { cacheArg: PostMultiplacerDevicesApiArg; devices: IDevice[] }
          >();

          for (const device of addedDevices) {
            const cacheArg = getDevicesCacheArgForDevice(device);
            const cacheKey = JSON.stringify(cacheArg);
            const group = devicesByCacheArg.get(cacheKey);
            if (group) {
              group.devices.push(device);
            } else {
              devicesByCacheArg.set(cacheKey, { cacheArg, devices: [device] });
            }
          }

          let patchesCount = 0;
          for (const { cacheArg, devices } of devicesByCacheArg.values()) {
            const patchResult = dispatch(
              openapi.util.updateQueryData("postMultiplacerDevices", cacheArg, (draft) => {
                draft.push(...devices);
              })
            );
            patchesCount += patchResult.patches.length;
          }

          if (patchesCount === 0) {
            dispatch(openapi.util.invalidateTags(["devices"]));
          }

          await queryFulfilled;
          if (spaceValidationRun.active) {
            dispatch(thunkRunSpaceValidations());
          }
          dispatch(thunkToast({ text: "Data er lagret", header: "Suksess", isSuccess: true }));
        } catch (error) {
          console.warn("Error adding device", error);
          dispatch(openapi.util.invalidateTags(["devices"]));
          dispatch(
            thunkToast({
              text: "Kunne ikke legge til komponent. Data ble hentet på nytt.",
              header: "Feil",
              isSuccess: false,
            })
          );
        }
      },
    },

    putMultiplacerDeviceUpdate: {
      async onQueryStarted(resp, { dispatch, queryFulfilled, getState }) {
        const updatedDevice = resp.body.device;
        const spaceValidationRun = (getState() as IRootState).design
          .spaceValidationRun as ISpaceValidationRun;
        const cacheKey = getDevicesCacheArgForDevice(updatedDevice);

        const patchResult = dispatch(
          openapi.util.updateQueryData("postMultiplacerDevices", cacheKey, (draft) => {
            draft.map((_device) => {
              if (_device._id === updatedDevice._id) {
                Object.assign(_device, updatedDevice);
              }
              return _device;
            });
          })
        );

        if (patchResult.patches.length === 0) {
          dispatch(openapi.util.invalidateTags(["devices"]));
        }

        try {
          await queryFulfilled;
          if (spaceValidationRun.active) {
            dispatch(thunkRunSpaceValidations());
          }
          dispatch(thunkToast({ text: "Data er lagret", header: "Suksess", isSuccess: true }));
        } catch (error) {
          console.warn("Error updating device", error);
          dispatch(openapi.util.invalidateTags(["devices"]));
          dispatch(
            thunkToast({
              text: "Kunne ikke lagre komponentendringen. Data ble hentet på nytt.",
              header: "Feil",
              isSuccess: false,
            })
          );
        }
      },
    },

    deleteMultiplacerDeviceDeleteDevice: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        const spaceValidationRun = (getState() as IRootState).design
          .spaceValidationRun as ISpaceValidationRun;
        const cacheArgs = getDeviceListCacheArgsContainingDevice(
          getState() as IRootState,
          args.deviceId
        );

        const patchResults = cacheArgs.map((cacheArg) =>
          dispatch(
            openapi.util.updateQueryData("postMultiplacerDevices", cacheArg, (draft) => {
              const index = draft.findIndex((device) => device._id === args.deviceId);
              if (index !== -1) {
                draft.splice(index, 1);
              }
            })
          )
        );

        try {
          await queryFulfilled;
          if (spaceValidationRun.active) {
            dispatch(thunkRunSpaceValidations());
          }
          dispatch(thunkToast({ text: "Data er lagret", header: "Suksess", isSuccess: true }));
        } catch (error) {
          console.warn("Error deleting device", error);
          patchResults.forEach((patchResult) => patchResult.undo());
          dispatch(openapi.util.invalidateTags(["devices"]));
          dispatch(
            thunkToast({
              text: "Kunne ikke slette komponent. Data ble hentet på nytt.",
              header: "Feil",
              isSuccess: false,
            })
          );
        }
      },
    },
    postMlPredictCeilingDevices: {
      async onQueryStarted(args, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as IRootState;
          const floorAndJob = state.design.floorAndJob;
          const activeRoom = selectHttpSingleRoomFull(state);

          const nrOfDevices = data?.length ?? 0;
          const deviceContext: IDeviceContext = {
            projectNo: String(args.body.projectNo),
            floor: floorAndJob?.floorName ?? "",
            roomName: activeRoom?.roomName,
            roomNumber: args.body.roomNr,
            description: "",
            compCode: "",
          };
          dispatch(
            addNonPublischedDevicesFromAlgosInRedux(convertMlBatchToDevices(data, deviceContext))
          );
          dispatch(
            thunkToast({
              text: `Predicting devices successful. ${nrOfDevices} devices created.`,
              header: "Suksess",
              isSuccess: true,
            })
          );
        } catch (e) {
          dispatch(
            thunkToast({
              text: "Predicting devices failed.",
              header: "Feil",
              isSuccess: false,
            })
          );
        }
      },
    },
  },
});
